import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField,
  Typography,
} from '@mui/material';
import { MushakService } from '../../services/mushak.service';
import { AuthService } from '../../services/auth.service';
import { MushakForm, MushakRegister } from '../../interfaces/indirectTax.interface';
import { useToast } from '../../context/ToastContext';
import { formatMoney } from '../../utils/format';
import { formatDate, firstOfMonthLocalIso, toLocalIsoDate } from '../../utils/dateUtils';
import { saveBlob } from '../../utils/saveBlob';

const TITLES: Record<MushakForm, string> = {
  '6.1': 'Mushak 6.1 - Purchase Register',
  '6.2': 'Mushak 6.2 - Sales Register',
};

interface Props {
  form: MushakForm;
}

const MushakRegisterPage: React.FC<Props> = ({ form }) => {
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution() as string;
  const purchases = form === '6.1';

  const [from, setFrom] = useState(firstOfMonthLocalIso());
  const [to, setTo] = useState(toLocalIsoDate());
  const [data, setData] = useState<MushakRegister | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    if (!from || !to) return;
    try {
      setLoading(true);
      setData(await MushakService.getRegister(form, institutionId, from, to));
    } catch (e) {
      setData(null);
      toast.showApiError(e, `Failed to load Mushak ${form} register`);
    } finally {
      setLoading(false);
    }
  }, [form, institutionId, from, to]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const doExport = async (format: 'xlsx' | 'csv') => {
    try {
      setExporting(true);
      const blob = await MushakService.exportRegister(form, institutionId, from, to, format);
      saveBlob(blob, `mushak-${form.replace('.', '-')}-${from}-to-${to}.${format}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to export register');
    } finally {
      setExporting(false);
    }
  };

  const num = (v: number) => formatMoney(v);

  return (
    <Box data-help-page="tax.mushakRegister.page" sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 0.5 }}>{TITLES[form]}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Built from the tax ledger for the dates below. Verify before filing on the NBR e-VAT portal.
      </Typography>

      <Box data-help-section="tax.mushakRegister.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
        <Box sx={{ flex: '1 1 180px', maxWidth: 220 }}>
          <TextField data-help="tax.mushakRegister.from" fullWidth size="small" type="date" label="From" value={from} InputLabelProps={{ shrink: true }}
            onChange={e => setFrom(e.target.value)} />
        </Box>
        <Box sx={{ flex: '1 1 180px', maxWidth: 220 }}>
          <TextField data-help="tax.mushakRegister.to" fullWidth size="small" type="date" label="To" value={to} InputLabelProps={{ shrink: true }}
            onChange={e => setTo(e.target.value)} />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button data-help="tax.mushakRegister.xlsx" variant="outlined" onClick={() => doExport('xlsx')} disabled={exporting || loading || !data}>Export XLSX</Button>
          <Button data-help="tax.mushakRegister.csv" variant="outlined" onClick={() => doExport('csv')} disabled={exporting || loading || !data}>Export CSV</Button>
        </Box>
      </Box>

      {loading && <CircularProgress />}

      {!loading && data && (
        <>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {data.institutionName} | BIN {data.bin || '(not set)'} | {formatDate(data.from)} to {formatDate(data.to)}
          </Typography>
          {data.notes.map(n => <Alert key={n} severity="info" sx={{ mb: 1 }}>{n}</Alert>)}

          <Paper sx={{ p: 2, overflowX: 'auto' }}>
            <Table data-help-section="tax.mushakRegister.table" size="small" data-testid="mushak-register-table">
              <TableHead>
                <TableRow>
                  <TableCell data-help="tax.mushakRegister.col.date">Date</TableCell>
                  <TableCell data-help="tax.mushakRegister.col.party">{purchases ? 'Supplier' : 'Customer'}</TableCell>
                  <TableCell data-help="tax.mushakRegister.col.bin">BIN</TableCell>
                  <TableCell data-help="tax.mushakRegister.col.document">{purchases ? 'Bill no' : 'Document no'}</TableCell>
                  {purchases && <TableCell data-help="tax.mushakRegister.col.supplierInvoice">Supplier invoice</TableCell>}
                  <TableCell data-help="tax.mushakRegister.col.description">Description</TableCell>
                  <TableCell data-help="tax.mushakRegister.col.value" align="right">Value</TableCell>
                  <TableCell data-help="tax.mushakRegister.col.sd" align="right">SD</TableCell>
                  <TableCell data-help="tax.mushakRegister.col.vat" align="right">VAT</TableCell>
                  <TableCell data-help="tax.mushakRegister.col.other" align="right">Other</TableCell>
                  <TableCell data-help="tax.mushakRegister.col.total" align="right">Total</TableCell>
                  {purchases && <TableCell data-help="tax.mushakRegister.col.recoverable">Recoverable</TableCell>}
                  <TableCell data-help="tax.mushakRegister.col.note">Note</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.rows.length === 0 && (
                  <TableRow><TableCell colSpan={purchases ? 13 : 11}>
                    <Typography color="text.secondary">No entries in this date range.</Typography>
                  </TableCell></TableRow>
                )}
                {data.rows.map((r, i) => (
                  <TableRow key={`${r.documentNumber}-${i}`}>
                    <TableCell>{formatDate(r.date)}</TableCell>
                    <TableCell>{r.partyName}</TableCell>
                    <TableCell>{r.partyBin}</TableCell>
                    <TableCell>{r.documentNumber}</TableCell>
                    {purchases && <TableCell>{r.supplierInvoiceNo}</TableCell>}
                    <TableCell>{r.description}</TableCell>
                    <TableCell align="right">{num(r.value)}</TableCell>
                    <TableCell align="right">{num(r.sdAmount)}</TableCell>
                    <TableCell align="right">{num(r.vatAmount)}</TableCell>
                    <TableCell align="right">{num(r.otherTaxAmount)}</TableCell>
                    <TableCell align="right">{num(r.total)}</TableCell>
                    {purchases && (
                      <TableCell>
                        {r.recoverable === null ? '' : <Chip size="small" label={r.recoverable ? 'Yes' : 'No'}
                          color={r.recoverable ? 'success' : 'default'} />}
                      </TableCell>
                    )}
                    <TableCell>{r.note}{r.source === 'LEGACY' && !r.note ? 'Legacy' : ''}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={purchases ? 6 : 5}><strong>Total</strong></TableCell>
                  <TableCell align="right" data-testid="mushak-total-value"><strong>{num(data.totals.value)}</strong></TableCell>
                  <TableCell align="right"><strong>{num(data.totals.sd)}</strong></TableCell>
                  <TableCell align="right" data-testid="mushak-total-vat"><strong>{num(data.totals.vat)}</strong></TableCell>
                  <TableCell align="right"><strong>{num(data.totals.other)}</strong></TableCell>
                  <TableCell align="right"><strong>{num(data.totals.total)}</strong></TableCell>
                  <TableCell colSpan={purchases ? 2 : 1}>
                    {purchases && (
                      <Typography variant="caption">
                        VAT recoverable {num(data.totals.vatRecoverable)} / non-recoverable {num(data.totals.vatNonRecoverable)}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default MushakRegisterPage;
